import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star, MapPin, X, Envelope, Key, Eye, EyeSlash } from "@phosphor-icons/react";

// Iter209bk — Sezione admin per gestire la casella Zoho di un operatore
// direttamente dalla scheda operatore (senza pagina "Gestione Email"):
//  - visualizza l'indirizzo attuale
//  - permette di cambiare la password (POST /admin/users/{uid}/email/change-password)
//  - permette di cambiare l'indirizzo (POST /admin/users/{uid}/email/change-address)
function OperatorEmailBox({ user, onUpdated }) {
  const current = user.zoho_email || "";
  // Iter209cm — Guida la vista su `zoho_email` (fonte di verita' sul
  // profilo). `zoho_account_id` resta usato per le API ma non deve
  // bloccare il rendering: se l'operatore ha gia' un indirizzo
  // (anche legacy senza account_id in locale), lo trattiamo come
  // casella associata e non mostriamo il form di creazione.
  const hasBox = !!current;
  const [newEmail, setNewEmail] = useState(current);
  const [savingEmail, setSavingEmail] = useState(false);
  // Iter209bx — Reset password auto-generata mostrata una sola volta.
  // (Iter209cn) resetPassword / revealedPwd rimossi: la UI ora apre un
  // form inline manuale, nessuna auto-generazione.
  // Iter209ch/ck — Stati del password editor + unlink spostati sotto
  // insieme agli altri hooks, SOPRA l'early return `!hasBox`.

  // Iter209bv — Stato per creare una nuova casella su operatori senza email.
  const suggestUsername = () => {
    const fn = (user.first_name || "").trim().toLowerCase();
    const ln = (user.last_name || "").trim().toLowerCase();
    const base = [fn, ln].filter(Boolean).join(".");
    return base.replace(/[^a-z0-9.]/g, "");
  };
  const [createUsername, setCreateUsername] = useState("");
  const [createPwd, setCreatePwd] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  // Iter209bz — Se create-mailbox risponde 409 "Casella già esistente",
  // permetti l'associazione diretta senza ricreare su Zoho.
  const [canLinkExisting, setCanLinkExisting] = useState(false);
  const [linking, setLinking] = useState(false);
  // Iter209ck/cn — TUTTI gli hook devono stare SOPRA l'early return
  // `if (!hasBox)` (react-hooks/rules-of-hooks).
  const [unlinking, setUnlinking] = useState(false);
  const [editingPwd, setEditingPwd] = useState(false);
  const [manualPwd, setManualPwd] = useState("");
  const [manualPwdConfirm, setManualPwdConfirm] = useState("");
  // Iter209dt — Toggle visibilità password casella (3 input: create + modify + confirm).
  // Default `false` (nascosta) in modo da ridurre il rischio di shoulder-surfing.
  // Flag separati per non "scoprire" un campo mentre si digita nell'altro.
  const [showCreatePwd, setShowCreatePwd] = useState(false);
  const [showManualPwd, setShowManualPwd] = useState(false);
  const [showManualPwdConfirm, setShowManualPwdConfirm] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  // Iter209cp — Stato per il test connessione IMAP reale (imappro.zoho.eu:993).
  const [testingImap, setTestingImap] = useState(false);
  const [imapTestResult, setImapTestResult] = useState(null); // {ok, message|error}

  useEffect(() => {
    setNewEmail(user.zoho_email || "");
    setCreateUsername(suggestUsername());
    setCreatePwd("");
    setCreateError("");
    setCanLinkExisting(false);
    setImapTestResult(null);
     
  }, [user.id, user.zoho_email]);

  const createMailbox = async () => {
    setCreateError("");
    setCanLinkExisting(false);
    const uname = createUsername.trim().toLowerCase();
    if (!uname) { setCreateError("Inserisci uno username"); return; }
    if (uname.includes("@")) { setCreateError("Solo lo username, senza dominio"); return; }
    if (createPwd.length < 8) { setCreateError("Password minimo 8 caratteri"); return; }
    setCreating(true);
    try {
      await api.post(`/admin/users/${user.id}/email/create-mailbox`, {
        username: uname, password: createPwd,
      });
      toast.success("Casella creata e associata");
      onUpdated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore creazione casella";
      const status = e?.response?.status;
      setCreateError(detail);
      // Il backend restituisce 409 con detail "Casella già esistente"
      // sia da provision_zoho_mailbox sia da create_operator_mailbox.
      if (status === 409 && /già esistente|gi[àa] esistent/i.test(detail)) {
        setCanLinkExisting(true);
      }
    } finally { setCreating(false); }
  };

  const linkExistingMailbox = async () => {
    const uname = createUsername.trim().toLowerCase();
    if (!uname) { setCreateError("Inserisci uno username"); return; }
    if (createPwd.length < 8) {
      setCreateError("La password IMAP è obbligatoria (min 8 caratteri)");
      return;
    }
    setLinking(true);
    try {
      await api.post(`/admin/users/${user.id}/email/link-existing`, {
        username: uname, password: createPwd,
      });
      toast.success("Casella esistente associata e password impostata");
      onUpdated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore associazione casella";
      const status = e?.response?.status;
      if (status === 409) {
        // Già associata a un altro operatore: mostra alert modale e
        // rimuovi il pulsante di link (non si può forzare da qui).
        window.alert(detail);
        setCanLinkExisting(false);
      }
      setCreateError(detail);
    } finally { setLinking(false); }
  };

  if (!hasBox) {
    return (
      <section data-testid="operator-email-box-empty">
        <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
          <Envelope size={14} weight="fill" /> Casella email @housetag.it
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          Questo operatore non ha ancora una casella. Puoi crearla ora
          scegliendo uno username: verrà creata su Zoho e associata al
          profilo.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Username</Label>
            <div className="flex gap-2 items-center">
              <Input
                value={createUsername}
                onChange={(e) => setCreateUsername(e.target.value.toLowerCase())}
                placeholder="nome.cognome"
                data-testid="operator-email-create-username"
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">@housetag.it</span>
            </div>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1">
              <Key size={12} weight="fill" /> Password casella
            </Label>
            {/* Iter209dt — Toggle visibilità con icona occhio. L'icona vive dentro
                un wrapper `relative`; l'Input ha padding-right per non sovrapporsi. */}
            <div className="relative">
              <Input
                type={showCreatePwd ? "text" : "password"}
                value={createPwd}
                onChange={(e) => setCreatePwd(e.target.value)}
                placeholder="Min 8 caratteri"
                autoComplete="new-password"
                className="pr-10"
                data-testid="operator-email-create-password"
              />
              <button
                type="button"
                onClick={() => setShowCreatePwd((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-slate-500 hover:text-slate-800 focus:outline-none"
                aria-label={showCreatePwd ? "Nascondi password" : "Mostra password"}
                tabIndex={-1}
                data-testid="operator-email-create-password-toggle"
              >
                {showCreatePwd ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
              </button>
            </div>
          </div>
          {createError && (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 flex flex-wrap items-center gap-3"
              data-testid="operator-email-create-error"
            >
              <span className="text-xs text-red-700 flex-1 min-w-[180px]">
                {createError}
              </span>
              {canLinkExisting && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={linkExistingMailbox}
                  disabled={linking || !createUsername}
                  className="border-red-300 text-red-800 hover:bg-red-100"
                  data-testid="operator-email-link-existing"
                >
                  {linking ? "Associazione…" : "Associa casella esistente"}
                </Button>
              )}
            </div>
          )}
          <div>
            <Button
              onClick={createMailbox}
              disabled={creating || !createUsername || createPwd.length < 8}
              className="ht-gradient border-0 text-white"
              data-testid="operator-email-create-submit"
            >
              {creating ? "Creazione…" : "Crea e associa casella"}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Comunica la password all'operatore da un canale sicuro. Potrà essere modificata in seguito da questa stessa scheda.
          </p>
        </div>
      </section>
    );
  }

  const changeEmail = async () => {
    if (!newEmail || newEmail === current) return;
    setSavingEmail(true);
    try {
      await api.post(`/admin/users/${user.id}/email/change-address`, { new_email: newEmail });
      toast.success("Indirizzo email aggiornato");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento email");
    } finally { setSavingEmail(false); }
  };

  // Iter209cn — Helper del password editor manuale (hook state hoisted sopra).
  const openPwdEditor = () => {
    setManualPwd("");
    setManualPwdConfirm("");
    setEditingPwd(true);
  };
  const cancelPwdEditor = () => {
    setEditingPwd(false);
    setManualPwd("");
    setManualPwdConfirm("");
  };
  const saveManualPwd = async () => {
    if (manualPwd.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri");
      return;
    }
    if (manualPwd !== manualPwdConfirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post(`/admin/users/${user.id}/email/set-password-local`, {
        password: manualPwd, confirm: manualPwdConfirm,
      });
      toast.success("Password IMAP salvata");
      setImapTestResult(null);
      cancelPwdEditor();
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio password");
    } finally { setSavingPwd(false); }
  };

  // Iter209cp — Verifica una connessione IMAP reale verso imappro.zoho.eu:993
  // con le credenziali salvate dell'operatore. Non modifica il DB.
  const testImapConnection = async () => {
    setTestingImap(true);
    setImapTestResult(null);
    try {
      const { data } = await api.post(
        `/admin/users/${user.id}/email/test-imap-connection`
      );
      setImapTestResult(data || { ok: false, error: "Risposta vuota dal server" });
    } catch (e) {
      setImapTestResult({
        ok: false,
        error: e?.response?.data?.detail || e?.message || "Errore di rete",
      });
    } finally { setTestingImap(false); }
  };

  // Iter209ch — Rimuovi l'associazione casella al profilo (NON cancella
  // da Zoho). Dopo l'unlink il form di creazione/associazione torna
  // disponibile automaticamente.
  const unlinkMailbox = async () => {
    if (!window.confirm("Dissociare la casella dal profilo?\n\nLa casella NON viene cancellata da Zoho: potrai riassociarla a un altro operatore oppure crearne una nuova.")) return;
    setUnlinking(true);
    try {
      await api.post(`/admin/users/${user.id}/email/unlink`);
      toast.success("Casella dissociata");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore dissociazione");
    } finally { setUnlinking(false); }
  };

  return (
    <section data-testid="operator-email-box">
      <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
        <Envelope size={14} weight="fill" /> Casella email @housetag.it
      </h3>

      {/* Iter209ci — Vista "casella attiva": solo l'indirizzo + 2 azioni.
          Nessun form di creazione/associazione visibile fin quando non
          si clicca "Dissocia casella". */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Indirizzo associato
        </div>
        <div
          className="font-mono text-base font-semibold text-slate-900 break-all mt-0.5"
          data-testid="operator-email-current-address"
        >
          {current}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <Label className="text-xs flex items-center gap-1">
            <Key size={12} weight="fill" /> Password IMAP
          </Label>
          {!editingPwd ? (
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Button
                onClick={openPwdEditor}
                variant="outline"
                data-testid="operator-email-password-reset"
              >
                Cambia password IMAP
              </Button>
              <Button
                onClick={testImapConnection}
                variant="outline"
                disabled={testingImap}
                data-testid="verify-imap-connection-btn"
              >
                {testingImap ? "Verifica…" : "Verifica connessione"}
              </Button>
              <p className="text-[10px] text-muted-foreground basis-full">
                Imposta manualmente la password già configurata sulla casella Zoho — non viene modificata da remoto. "Verifica connessione" apre un IMAP SSL reale verso imappro.zoho.eu:993.
              </p>
              {imapTestResult && imapTestResult.ok && (
                <div
                  className="basis-full mt-1 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                  data-testid="imap-test-result-success"
                >
                  Connessione riuscita
                  {imapTestResult.email ? ` (${imapTestResult.email})` : ""}
                </div>
              )}
              {imapTestResult && !imapTestResult.ok && (
                <div
                  className="basis-full mt-1 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 break-words"
                  data-testid="imap-test-result-error"
                >
                  Connessione fallita: {imapTestResult.error || "errore sconosciuto"}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2 mt-1 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="operator-email-password-editor">
              <div className="relative">
                <Input
                  type={showManualPwd ? "text" : "password"}
                  value={manualPwd}
                  onChange={(e) => setManualPwd(e.target.value)}
                  placeholder="Nuova password IMAP (min 8 caratteri)"
                  autoComplete="new-password"
                  className="pr-10"
                  data-testid="operator-email-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowManualPwd((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-slate-500 hover:text-slate-800 focus:outline-none"
                  aria-label={showManualPwd ? "Nascondi password" : "Mostra password"}
                  tabIndex={-1}
                  data-testid="operator-email-password-toggle"
                >
                  {showManualPwd ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>
              <div className="relative">
                <Input
                  type={showManualPwdConfirm ? "text" : "password"}
                  value={manualPwdConfirm}
                  onChange={(e) => setManualPwdConfirm(e.target.value)}
                  placeholder="Conferma password"
                  autoComplete="new-password"
                  className="pr-10"
                  data-testid="operator-email-password-confirm"
                />
                <button
                  type="button"
                  onClick={() => setShowManualPwdConfirm((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-slate-500 hover:text-slate-800 focus:outline-none"
                  aria-label={showManualPwdConfirm ? "Nascondi password" : "Mostra password"}
                  tabIndex={-1}
                  data-testid="operator-email-password-confirm-toggle"
                >
                  {showManualPwdConfirm ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={saveManualPwd}
                  disabled={savingPwd || manualPwd.length < 8 || manualPwd !== manualPwdConfirm}
                  className="ht-gradient border-0 text-white"
                  data-testid="operator-email-password-save"
                >
                  {savingPwd ? "Salvataggio…" : "Salva password"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={cancelPwdEditor}
                  data-testid="operator-email-password-cancel"
                >
                  Annulla
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                La password viene salvata SOLO nel database locale (`zoho_email_password`), senza chiamate a Zoho. Usa la stessa password già impostata sulla casella.
              </p>
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-slate-100">
          <Button
            variant="outline"
            onClick={unlinkMailbox}
            disabled={unlinking}
            className="border-red-200 text-red-700 hover:bg-red-50"
            data-testid="operator-email-unlink"
          >
            {unlinking ? "Dissociazione…" : "Dissocia casella"}
          </Button>
          <p className="text-[10px] text-muted-foreground mt-1">
            La casella non viene cancellata da Zoho: dopo la dissociazione potrai riassociarla a un altro operatore oppure crearne una nuova.
          </p>
        </div>
      </div>
    </section>
  );
}

function StarRating({ value = 0, onChange, testId }) {
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button"
          onClick={() => onChange(n === value ? 0 : n)}
          className="p-0.5 hover:scale-110 transition-transform"
          aria-label={`${n} stelle`}
          data-testid={`${testId}-star-${n}`}>
          <Star size={22} weight={n <= value ? "fill" : "regular"} color={n <= value ? "#f59e0b" : "#94a3b8"} />
        </button>
      ))}
      <span className="text-xs text-muted-foreground ml-1 w-8">{value || "—"}</span>
    </div>
  );
}

function ProvinceBulkPicker({ operatorId, disabled, onChange }) {
  const [provinces, setProvinces] = useState([]);
  const [prov, setProv] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/cities/provinces").then(r => setProvinces(r.data || [])).catch(() => {});
  }, []);

  const run = async (action) => {
    const p = provinces.find(x => x.code === prov);
    if (!p) return;
    const msg = action === "add"
      ? `Aggiungere tutti i ${p.count} comuni della provincia ${p.code}?`
      : `Rimuovere tutti i comuni della provincia ${p.code} da questo operatore?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await api.post(`/users/${operatorId}/coverage-cities/province`, { province: prov, action });
      onChange(r.data?.cities || []);
      toast.success(action === "add" ? `${r.data.changed} comuni aggiunti (${p.code})` : `${r.data.changed} comuni rimossi (${p.code})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento provincia");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="coverage-province-picker">
      <select
        value={prov} onChange={e => setProv(e.target.value)} disabled={disabled || busy}
        className="h-9 border rounded-md px-2 text-sm bg-white"
        data-testid="coverage-province-select"
      >
        <option value="">Provincia…</option>
        {provinces.map(p => <option key={p.code} value={p.code}>{p.code} ({p.count} comuni)</option>)}
      </select>
      <Button type="button" size="sm" variant="outline" disabled={!prov || disabled || busy} onClick={() => run("add")} data-testid="coverage-province-add-btn">
        Aggiungi tutti i comuni
      </Button>
      <Button type="button" size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={!prov || disabled || busy} onClick={() => run("remove")} data-testid="coverage-province-remove-btn">
        Rimuovi tutti i comuni
      </Button>
    </div>
  );
}

function CoverageCitiesEditor({ operatorId, testId }) {
  const [cities, setCities] = useState([]);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/users/${operatorId}/coverage-cities`).then(r => setCities(r.data?.cities || []));
  }, [operatorId]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2 || /[,;\n]/.test(q)) { setSuggestions([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/public/cities/search?q=${encodeURIComponent(q)}&limit=8`)
        .then(r => setSuggestions(r.data || []))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const addCity = async (s) => {
    if (!s?.name) return;
    const label = s.province ? `${s.name} (${s.province})` : s.name;
    const keyOf = (c) => { const m = /^(.+?)\s*\(([A-Za-z]{2})\)$/.exec(c); return m ? `${m[1].toLowerCase()}|${m[2].toUpperCase()}` : `${c.toLowerCase()}|`; };
    if (cities.some(c => keyOf(c) === keyOf(label))) {
      toast.info("Città già presente");
      setQuery(""); setSuggestions([]);
      return;
    }
    const next = [...cities, label];
    setSaving(true);
    try {
      const r = await api.put(`/users/${operatorId}/coverage-cities`, { cities: next });
      setCities(r.data?.cities || next);
      setQuery(""); setSuggestions([]);
    } catch (e) {
      toast.error("Errore salvataggio città");
    } finally { setSaving(false); }
  };

  const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

  const addMany = async (text) => {
    const tokens = [...new Set(text.split(/[,;\n]+/).map(t => t.trim()).filter(Boolean))];
    if (!tokens.length) return;
    setSaving(true);
    try {
      const lookup = async (t) => {
        const pick = (r) => (r.data || []).find(s => norm(s.name) === norm(t))?.name || null;
        const exact = await api.get(`/public/cities/search?q=${encodeURIComponent(t)}&limit=20`).then(pick);
        if (exact || t.length < 4) return exact;
        // ricerca accent-insensitive: "forli" → prefisso "forl" → "Forlì"
        const prefix = t.slice(0, Math.max(3, t.length - 2));
        return api.get(`/public/cities/search?q=${encodeURIComponent(prefix)}&limit=50`).then(pick);
      };
      const found = await Promise.all(tokens.map(t => lookup(t).catch(() => null)));
      const notFound = tokens.filter((_, i) => !found[i]);
      const toAdd = found.filter(n => n && !cities.some(c => norm(c) === norm(n)));
      if (toAdd.length) {
        const next = [...cities, ...new Set(toAdd)];
        const r = await api.put(`/users/${operatorId}/coverage-cities`, { cities: next });
        setCities(r.data?.cities || next);
        toast.success(`${new Set(toAdd).size} ${toAdd.length === 1 ? "città aggiunta" : "città aggiunte"}`);
      } else if (!notFound.length) {
        toast.info("Città già presenti");
      }
      if (notFound.length) toast.error(`Comuni non riconosciuti, non aggiunti: ${notFound.join(", ")}`, { duration: 8000 });
      setQuery(""); setSuggestions([]);
    } catch (e) {
      toast.error("Errore salvataggio città");
    } finally { setSaving(false); }
  };

  const removeCity = async (name) => {
    const next = cities.filter(c => c !== name);
    setSaving(true);
    try {
      const r = await api.put(`/users/${operatorId}/coverage-cities`, { cities: next });
      setCities(r.data?.cities || next);
    } catch (e) {
      toast.error("Errore aggiornamento");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="relative">
        <div className="relative">
          <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onPaste={e => {
              const txt = e.clipboardData.getData("text");
              if (/[,;\n]/.test(txt)) { e.preventDefault(); addMany(txt); }
            }}
            onKeyDown={e => { if (e.key === "Enter" && /[,;]/.test(query)) { e.preventDefault(); addMany(query); } }}
            placeholder="Cerca un comune, oppure incolla più comuni separati da virgola…"
            className="pl-9"
            data-testid="coverage-city-search"
            disabled={saving}
          />
        </div>
        {suggestions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full border rounded-md bg-white shadow-lg max-h-56 overflow-auto"
               data-testid="coverage-city-suggestions">
            {suggestions.map(s => (
              <button key={`${s.name}-${s.province}`} type="button"
                onClick={() => addCity(s)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b last:border-b-0 flex items-center justify-between"
                data-testid={`coverage-city-suggestion-${s.name}`}>
                <span className="font-medium">{s.name}</span>
                <span className="text-xs text-muted-foreground">{s.province}{s.cap ? ` · ${s.cap}` : ""}</span>
              </button>
            ))}
          </div>
        )}
        {loading && <div className="text-xs text-muted-foreground mt-1">Ricerca…</div>}
        {/[,;]/.test(query) && (
          <div className="text-xs text-indigo-700 mt-1" data-testid="coverage-city-bulk-hint">Premi Invio per aggiungere tutti i comuni elencati</div>
        )}
      </div>

      <ProvinceBulkPicker operatorId={operatorId} disabled={saving} onChange={setCities} />

      {cities.length === 0 ? (
        <div className="text-sm text-muted-foreground italic py-3 border border-dashed rounded text-center">
          Nessuna città configurata. In assegnazione automatica per i sopralluoghi non riceverà lavori.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 max-h-56 overflow-y-auto pr-1" data-testid="coverage-city-chips">
          {cities.slice().sort((a, b) => a.localeCompare(b, "it", { sensitivity: "base" })).map(c => (
            <span key={c}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[color:hsl(var(--surface-alt))] border rounded-full text-sm"
              data-testid={`coverage-city-chip-${c}`}>
              <MapPin size={12} className="text-slate-500" />
              {c}
              <button type="button" onClick={() => removeCity(c)}
                className="ml-1 text-slate-400 hover:text-red-500"
                aria-label={`Rimuovi ${c}`}
                data-testid={`coverage-city-remove-${c}`}>
                <X size={12} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OperatorDetailDialog({ user, onClose, onSaved }) {
  const open = !!user;
  const [services, setServices] = useState([]);
  const [scores, setScores] = useState({});
  const [compensations, setCompensations] = useState({});
  const [compensationsType, setCompensationsType] = useState({});
  const [operatorType, setOperatorType] = useState("in");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setOperatorType(user.operator_type || "in");
    Promise.all([
      api.get("/services"),
      api.get(`/users/${user.id}/service-scores`),
    ]).then(([s, sc]) => {
      setServices(s.data || []);
      setScores(sc.data?.scores || {});
      setCompensations(sc.data?.compensations || {});
      setCompensationsType(sc.data?.compensations_type || {});
    });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, {
        operator_type: operatorType,
      });
      await api.put(`/users/${user.id}/service-scores`, {
        scores,
        compensations,
        compensations_type: compensationsType,
      });
      toast.success("Scheda operatore salvata");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Scheda Operatore · {user.first_name} {user.last_name}</DialogTitle>
          <DialogDescription>{user.email} · {user.role}{user.is_supervisor ? " · Supervisor" : ""}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <section>
            <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Contratto</h3>
            <div>
              <Label>Tipo operatore</Label>
              <select value={operatorType} onChange={e => setOperatorType(e.target.value)}
                className="w-full h-10 border rounded px-3 text-sm bg-background" data-testid="detail-operator-type">
                <option value="in">IN — Dipendente interno (nessun compenso)</option>
                <option value="out">OUT — Freelance esterno (con compensi per servizio)</option>
              </select>
            </div>
          </section>

          {/* Iter209bk — Modifica casella email @housetag.it (via Zoho API) */}
          <OperatorEmailBox user={user} onUpdated={onSaved} />

          {user.role === "site_operator" && (
            <section>
              <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-1">Città di competenza</h3>
              <p className="text-xs text-muted-foreground mb-3">
                In un servizio con sopralluogo, l'assegnazione automatica considera solo i site operator abilitati per la città dell'immobile.
                Se nessuno è abilitato in quella città (nome + provincia), il sopralluogo resta all'admin.
              </p>
              <CoverageCitiesEditor operatorId={user.id} testId="coverage-cities-editor" />
            </section>
          )}

          <section>
            <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-1">Punteggio e compenso per servizio</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Punteggio da 1 (base) a 5 (esperto) — priorità di assegnazione automatica (default 3). {operatorType === "out" ? "Il compenso viene pagato all'operatore ogni volta che completa quel servizio: € fisso oppure % sul totale netto della pratica." : "Il compenso si applica solo se l'operatore è di tipo OUT."}
            </p>
            {services.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Nessun servizio configurato.</div>
            ) : (
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[color:hsl(var(--surface-alt))]"><tr className="text-left">
                    <th className="py-2 px-3">Servizio</th>
                    <th className="py-2 px-3">Categoria</th>
                    <th className="py-2 px-3">Punteggio</th>
                    <th className="py-2 px-3 text-right pr-4">Compenso</th>
                  </tr></thead>
                  <tbody>
                    {services.map(s => {
                      const ctype = compensationsType[s._id] || "eur";
                      const disabled = operatorType !== "out";
                      return (
                      <tr key={s._id} className="border-t" data-testid={`svc-score-row-${s._id}`}>
                        <td className="py-2 px-3 font-medium">{s.name}</td>
                        <td className="py-2 px-3 text-xs text-muted-foreground">{s.category}</td>
                        <td className="py-2 px-3">
                          <StarRating
                            value={scores[s._id] || 0}
                            onChange={(v) => setScores({ ...scores, [s._id]: v })}
                            testId={`stars-${s._id}`}
                          />
                        </td>
                        <td className="py-2 px-3 text-right pr-4">
                          <div className="flex items-center justify-end gap-1.5">
                            <input type="number" min="0" step="0.01"
                              value={compensations[s._id] ?? ""}
                              onChange={e => setCompensations({ ...compensations, [s._id]: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                              disabled={disabled}
                              placeholder="0.00"
                              className="w-24 h-8 border rounded px-2 text-xs font-mono text-right disabled:bg-gray-50 disabled:text-gray-400"
                              data-testid={`compensation-${s._id}`} />
                            <div className="inline-flex border rounded overflow-hidden" data-testid={`compensation-type-${s._id}`}>
                              <button type="button"
                                onClick={() => !disabled && setCompensationsType({ ...compensationsType, [s._id]: "eur" })}
                                disabled={disabled}
                                className={`px-2 h-8 text-xs font-medium transition-colors ${ctype === "eur" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"} disabled:opacity-50 disabled:cursor-not-allowed`}
                                data-testid={`compensation-type-eur-${s._id}`}
                                aria-pressed={ctype === "eur"}
                                title="Compenso fisso in Euro">€</button>
                              <button type="button"
                                onClick={() => !disabled && setCompensationsType({ ...compensationsType, [s._id]: "pct" })}
                                disabled={disabled}
                                className={`px-2 h-8 text-xs font-medium border-l transition-colors ${ctype === "pct" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"} disabled:opacity-50 disabled:cursor-not-allowed`}
                                data-testid={`compensation-type-pct-${s._id}`}
                                aria-pressed={ctype === "pct"}
                                title="Percentuale sul totale netto della pratica">%</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="ht-gradient border-0" data-testid="save-operator-detail">
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
